import type {
  OrgBillingUsage,
  OrgBilling,
  OrganizationFrame,
  OrganizationUser,
} from "@kontextwerk/control-web-client"
import type { PermissionState } from "@kontextwerk/control-web-frame/logic/permissions/model"
import type { KehrblattPlanCapabilities } from "../_shared/_logic/org-billing-plan-capabilities"
import { getContext, setContext } from "svelte"
import type { PageNavigationEntry } from "@kontextwerk/web-sdk/core/navigation/types"

export type OrganizationNavigationMetadata = {
  basePath: string
  ready: boolean
  pages: PageNavigationEntry[]
}

const NAVIGATION_METADATA_CONTEXT = Symbol("kehrblatt-organization-navigation-metadata")

/** Subviews read the same authorized metadata that the organization frame renders. */
export const provideOrganizationNavigationMetadata = (
  getMetadata: () => OrganizationNavigationMetadata,
) => setContext(NAVIGATION_METADATA_CONTEXT, getMetadata)

export const getOrganizationNavigationMetadata = () =>
  getContext<(() => OrganizationNavigationMetadata) | undefined>(NAVIGATION_METADATA_CONTEXT)

type PlanCardPricePresentation = {
  additionalPriceDetailLines?: Array<{
    key: string
    label: string
    value: string
    insertAfterMetric?: string | null
    insertBeforeMetric?: string | null
  }> | null
  headlineMetric?: string | null
  headlineUnitLabel?: string | null
  showCreditAmountInPriceDetails?: boolean
  priceDetailMetricOrder?: string[] | null
  hiddenPriceDetailMetrics?: string[] | null
  usageNote?: string | null
  usageNotesBySlug?: Record<string, string> | null
  usageNotesByPlanId?: Record<string, string> | null
}

export type OrgaViewFrameContext = {
  permissions: PermissionState
  organization: OrganizationFrame | null
  organizationId: string | null
  loading: boolean
  error: string | null
  onRetry: (() => void) | undefined
  refreshOrganization: () => Promise<OrganizationFrame | null>
  onUsersUpdated: (users: OrganizationUser[]) => void | Promise<void>
  onNotificationsChanged: () => void
  refreshKey: number
  onFeedbackCreated: () => void
  billingMode: "stripe" | "none"
  billingAvailable: boolean
  billingEnabled: boolean
  billingRequiresCheckout: boolean
  orgBilling: OrgBilling | null
  billingStateLoading: boolean
  billingStateError: string | null
  billingStateResolved: boolean
  isFreeTrialExperience: boolean
  trialRestrictionsActive: boolean
  hasHistoricalConsumption: boolean
  usagePreview: OrgBillingUsage | null
  planCardPresentation?: PlanCardPricePresentation | null
  subscriptionRestrictionActive: boolean
  tariffCapabilities: KehrblattPlanCapabilities
  voiceAllowed: boolean
  currentUserId: string | null
  currentUserRole: string | null
  currentUserOrganizationId: string | null
  onOrganizationUpdated: (event: CustomEvent<Partial<OrganizationFrame>>) => void
  onDeleteOrganization: () => void | Promise<void>
}
